// Drawing on the map (issue 1032, design/28 §4): the toolbar while drawing is on, the line of text being typed where
// it was placed on the board, and the drawings said in words for a screen reader (the canvas is one picture). The
// canvas draws the marks; nothing here computes from anything the store was not given.
import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { flushSync } from 'react-dom';
import { DRAW_LIMITS, DRAW_WIDTHS, TEXT_SIZES, type DrawWidth, type Drawing, type TextSize } from '@dragons-table/vtt-shared';
import { useShell } from '../shell/context';
import { useTableState } from '../table/TableState';
import { useMayAct } from '../table/mayAct';
import { DRAW_SWATCHES, drawTool, type DrawDraft, type DrawMode } from './drawTool';
import { lastOwn } from './drawGeometry';
import { StampList, StampNameForm } from './StampOverlays';
import { typingIn } from './typing';
import { pressWord } from '../shell/device';
import { BTN } from '../shell/ui';

const btn = BTN;
const on = 'border-amber-500 text-amber-300';
const MODES: Array<[DrawMode, string]> = [['stroke', 'Freehand'], ['line', 'Line'], ['rect', 'Rectangle'], ['ellipse', 'Ellipse'], ['text', 'Text'], ['erase', 'Erase'], ['stampBox', 'Save as stamp']];
const SWATCH_NAMES = ['Your colour', 'Red', 'Blue', 'Green', 'White', 'Black'];
const WIDTH_NAMES: Record<DrawWidth, string> = { 2: 'Thin', 4: 'Medium', 8: 'Thick' };
const SIZE_NAMES: Record<TextSize, string> = { 16: 'Small text', 24: 'Medium text', 36: 'Large text' };

export function useDraw(sync = false): DrawDraft {
  const [d, setD] = useState(drawTool.get());
  // `sync`: render inside the change itself (the text box, qa-runs 22). Placed by a click on the board, the box must
  // exist and hold the focus before that click's handler returns, or the keys typed straight after it reach the board.
  useEffect(() => drawTool.subscribe(sync ? (next) => flushSync(() => setD(next)) : setD), [sync]);
  return d;
}

const hint = (d: DrawDraft): string => {
  switch (d.mode) {
    case 'erase': return `${pressWord()} one of your drawings to erase it.`;
    case 'text': return `${pressWord()} where the text goes.`;
    case 'stampBox': return d.box ? 'Name the stamp.' : 'Drag a box around your own drawings to keep them as a stamp.';
    case 'stamp': return d.stamp ? `${pressWord()} the map to put down "${d.stamp.name}".` : 'Pick a stamp from your list.';
    default: return 'Drag on the map.';
  }
};

/** The toolbar (design/28 §4.3): the five kinds and Erase, six colours, three widths, three text sizes, Erase all of mine. */
export function DrawToolbar() {
  const { act, meId, store, viewingAs, role } = useTableState();
  const mayAct = useMayAct();
  const { table } = useShell();
  const d = useDraw();
  const drawings = store.state.drawings ?? [];
  // Planning (the DM layer's drawings) is the `map` power's (issue 1160), on the DM's board.
  const dm = role === 'dm' && mayAct('draw.add');
  const own = (table.me.colour ?? '#d4af37').toLowerCase();
  const swatches = [own, ...DRAW_SWATCHES];
  const colour = d.colour || own;
  const mine = drawings.some((x) => x.ownerId === meId);
  // Stamps (issue 1034): the library's list, open or shut; and what was last said about a save, rename or delete.
  const [listOpen, setListOpen] = useState(false);
  const [said, setSaid] = useState('');
  const marking = d.mode !== 'erase' && d.mode !== 'stampBox' && d.mode !== 'stamp';
  // Escape leaves drawing; Ctrl+Z erases this person's most recent drawing on the scene (design/28 §4.2, §4.5).
  const drawingsRef = useRef(drawings); drawingsRef.current = drawings;
  useEffect(() => {
    if (!d.on) return undefined;
    const onKey = (e: KeyboardEvent) => {
      if (typingIn(e.target)) return;
      if (e.key === 'Escape') { e.preventDefault(); drawTool.stop(); return; }
      if ((e.ctrlKey || e.metaKey) && !e.shiftKey && e.key.toLowerCase() === 'z') {
        e.preventDefault();
        const last = lastOwn(drawingsRef.current, meId);
        if (last) act({ kind: 'draw.erase', id: last.id });
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [d.on, meId, act]);
  if (!d.on || viewingAs) return null;
  return (
    <div className="pointer-events-auto flex max-w-full flex-wrap items-center gap-2 self-start rounded bg-stone-900/95 px-3 py-2 text-xs text-stone-200" data-testid="draw-toolbar" role="toolbar" aria-label="Draw on the map">
      <span className="font-medium text-amber-300">Draw</span>
      <span role="radiogroup" aria-label="Drawing tool" className="flex flex-wrap gap-0.5">
        {MODES.map(([m, label]) => (
          <button key={m} type="button" role="radio" aria-checked={d.mode === m} aria-label={label} data-testid={`draw-mode-${m}`} className={`${btn} ${d.mode === m ? on : ''}`} onClick={() => { setSaid(''); drawTool.change({ mode: m }); }}>{label}</button>
        ))}
      </span>
      <button type="button" className={`${btn} ${listOpen || d.mode === 'stamp' ? on : ''}`} aria-expanded={listOpen} data-testid="draw-stamps" onClick={() => setListOpen((o) => !o)}>Stamps</button>
      {marking && <span role="radiogroup" aria-label="Colour" className="flex gap-1">
        {swatches.map((c, k) => (
          <button key={`${k}${c}`} type="button" role="radio" aria-checked={colour === c} aria-label={SWATCH_NAMES[k]} data-testid={`draw-colour-${k}`}
            className={`h-5 w-5 rounded-full border-2 ${colour === c ? 'border-amber-400' : 'border-stone-600'}`} style={{ background: c }} onClick={() => drawTool.change({ colour: k === 0 ? '' : c })} />
        ))}
      </span>}
      {!marking ? null : d.mode === 'text' ? (
        <span role="radiogroup" aria-label="Text size" className="flex gap-0.5">
          {TEXT_SIZES.map((s) => <button key={s} type="button" role="radio" aria-checked={d.size === s} aria-label={SIZE_NAMES[s]} data-testid={`draw-size-${s}`} className={`${btn} ${d.size === s ? on : ''}`} onClick={() => drawTool.change({ size: s })}>{s === 16 ? 'S' : s === 24 ? 'M' : 'L'}</button>)}
        </span>
      ) : (
        <span role="radiogroup" aria-label="Thickness" className="flex gap-0.5">
          {DRAW_WIDTHS.map((w) => (
            <button key={w} type="button" role="radio" aria-checked={d.width === w} aria-label={WIDTH_NAMES[w]} data-testid={`draw-width-${w}`} className={`${btn} ${d.width === w ? on : ''}`} onClick={() => drawTool.change({ width: w })}>
              <span className="inline-block w-4 rounded-full bg-current align-middle" style={{ height: w / 2 + 1 }} />
            </button>
          ))}
        </span>
      )}
      {dm && (
        <label className="flex items-center gap-1" title="What you draw now is on your planning layer: no player sees it">
          <input type="checkbox" data-testid="draw-planning" checked={d.planning} onChange={(e) => drawTool.change({ planning: e.target.checked })} />
          Planning (only you see it)
        </label>
      )}
      <button type="button" className={btn} data-testid="draw-erase-mine" disabled={!mine} onClick={() => act({ kind: 'draw.eraseMine', ids: [] })}>Erase all of mine</button>
      <span className="text-stone-400" data-testid="draw-hint">{hint(d)} {d.mode === 'stampBox' ? '' : dm && d.planning ? 'Only you see what you draw.' : 'Everyone on this map sees it.'}</span>
      <button type="button" className={btn} onClick={() => drawTool.stop()}>Done</button>
      <StampNameForm d={d} onSaid={setSaid} />
      {said && <span role="status" className="text-amber-200" data-testid="draw-stamp-said">{said}</span>}
      {listOpen && <StampList d={d} onSaid={setSaid} />}
    </div>
  );
}

/** The line of text being typed, on the board where it was placed: Enter commits it, Escape abandons it (design/28 §1.4). */
export function DrawTextInput() {
  const { act, meId, store } = useTableState();
  const { table } = useShell();
  const d = useDraw(true);
  const [value, setValue] = useState('');
  const ref = useRef<HTMLInputElement>(null);
  const at = d.on && d.mode === 'text' ? d.text : null;
  // Focus as the box opens, before the browser paints (qa-runs 22): a focus left to a timer let the first keys typed
  // straight after the click go to the board, and the text lost them.
  useLayoutEffect(() => { setValue(''); if (at) ref.current?.focus(); }, [at?.x, at?.y]); // eslint-disable-line react-hooks/exhaustive-deps
  if (!at) return null;
  const colour = d.colour || (table.me.colour ?? '#d4af37').toLowerCase();
  const full = (store.state.drawings ?? []).length >= DRAW_LIMITS.drawings;
  const commit = () => {
    const text = value.trim();
    if (!text) { drawTool.placeText(null); return; }
    const drawing: Drawing = { id: crypto.randomUUID(), ownerId: meId, kind: 'text', points: [at.x, at.y], colour, width: d.width, text: text.slice(0, DRAW_LIMITS.text), size: d.size, ...(d.planning && table.me.role === 'dm' ? { planning: true as const } : {}) };
    act({ kind: 'draw.add', drawing }, (reply) => { if (reply.ok) drawTool.placeText(null); });
  };
  return (
    <input ref={ref} data-testid="draw-text-input" aria-label="Text to draw" maxLength={DRAW_LIMITS.text} value={value} placeholder={full ? 'This map is full' : 'Type, then Enter'}
      className="pointer-events-auto absolute z-20 min-w-40 rounded border border-amber-500 bg-stone-900/90 px-1 text-stone-100 outline-none"
      style={{ left: at.sx, top: at.sy, fontSize: Math.max(12, Math.min(28, d.size * 0.75)), color: colour }}
      onChange={(e) => setValue(e.target.value)}
      onKeyDown={(e) => {
        e.stopPropagation();
        if (e.key === 'Enter') { e.preventDefault(); commit(); }
        if (e.key === 'Escape') { e.preventDefault(); drawTool.placeText(null); }
      }} />
  );
}

const KIND_WORDS: Record<Drawing['kind'], string> = { stroke: 'A freehand line', line: 'A line', rect: 'A rectangle', ellipse: 'An ellipse', text: 'Text' };

/** The drawings on this map, in words, for a screen reader: the board itself is a single picture. */
export function DrawingsList() {
  const { store, meId } = useTableState();
  const { table } = useShell();
  const drawings = store.state.drawings ?? [];
  if (!drawings.length) return null;
  const who = (id: string) => {
    if (!id) return 'somebody who has left';
    if (id === meId) return 'you';
    const m = table.members.find((x) => x.id === id);
    return m?.handle ? `@${m.handle}` : m?.displayName ?? 'someone';
  };
  return (
    <ul className="sr-only" data-testid="drawings" aria-label="Drawings on this map">
      {drawings.map((x) => (
        <li key={x.id} data-testid="drawing" data-id={x.id} data-kind={x.kind} data-mine={x.ownerId === meId ? 'true' : 'false'} data-planning={x.planning ? 'true' : 'false'}>
          {KIND_WORDS[x.kind]}{x.kind === 'text' ? ` "${x.text}"` : ''}, by {who(x.ownerId)}{x.planning ? ', on your planning layer' : ''}
        </li>
      ))}
    </ul>
  );
}
